import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { formatMxn } from "../../shared/metrics";
import type { CatalogSnapshot, SessionPublic, SessionSummary } from "../../shared/model";
import { clientApi } from "../api";
import { Brand, StatusBadge } from "../components/Brand";
import { ErrorCard, Loading } from "../components/Loading";

function AdminAccessFallback({ setupRequired }: { setupRequired: boolean }) {
  return (
    <main className="admin-login-page" id="main">
      <header className="admin-header">
        <Brand />
        <div>
          <span className="eyebrow">PANEL PRIVADO</span>
          <strong>Acceso administrativo</strong>
        </div>
      </header>
      <section className="admin-login-card">
        <span className="icon-box" aria-hidden="true">✦</span>
        {setupRequired ? (
          <>
            <span className="eyebrow">CONFIGURACIÓN PENDIENTE</span>
            <h1>Falta activar Cloudflare Access</h1>
            <p>
              La ruta todavía llega directamente a la aplicación. Access debe protegerla antes de
              que pueda mostrar el formulario de correo y enviar códigos de un solo uso.
            </p>
            <aside>
              <strong>El panel sigue bloqueado</strong>
              <span>
                La API administrativa no acepta solicitudes mientras falten la aplicación de
                Access y su identificador de audiencia.
              </span>
            </aside>
            <a
              className="hw-btn hw-btn--primary"
              href="https://one.dash.cloudflare.com/"
              rel="noreferrer"
              target="_blank"
            >
              Abrir Cloudflare Zero Trust ↗
            </a>
            <small>
              Después de configurar Access, recarga esta dirección. El formulario de correo
              aparecerá antes de que cargue este sitio.
            </small>
          </>
        ) : (
          <>
            <span className="eyebrow">CLOUDFLARE ACCESS</span>
            <h1>Acceso administrativo requerido</h1>
            <p>
              No recibimos una sesión válida de Cloudflare Access para abrir este panel.
            </p>
            <aside>
              <strong>Importante</strong>
              <span>
                El código sólo llegará si el correo está incluido en la lista de correos
                permitidos.
              </span>
            </aside>
            <button
              className="hw-btn hw-btn--primary"
              onClick={() => location.reload()}
              type="button"
            >
              Comprobar acceso de nuevo
            </button>
          </>
        )}
      </section>
    </main>
  );
}

function statusLabel(status: SessionSummary["status"]): string {
  return {
    draft: "Borrador",
    open: "Abierta",
    closed: "Cerrada",
    expired: "Expirada",
  }[status];
}

function CatalogEditor({
  session,
  onSaved,
}: {
  session: SessionPublic;
  onSaved: () => void;
}) {
  const [catalog, setCatalog] = useState<CatalogSnapshot>(session.catalog);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await clientApi.updateCatalog(session.slug, {
        ...catalog,
        verifiedAt: new Date().toISOString().slice(0, 10),
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="catalog-editor">
      <div className="admin-section-heading">
        <div>
          <span className="eyebrow">PRECIOS DE REFERENCIA</span>
          <h3>Catálogo congelado al abrir</h3>
        </div>
        <button className="hw-btn hw-btn--primary" disabled={saving} onClick={() => void save()} type="button">
          {saving ? "Guardando…" : "Guardar precios"}
        </button>
      </div>
      <div className="catalog-grid">
        {catalog.services.map((service, index) => (
          <label key={service.id}>
            <span>
              <b>{service.shortName}</b>
              <small>{service.plan}</small>
            </span>
            <span className="money-input">
              $
              <input
                inputMode="decimal"
                min="0"
                onChange={(event) => {
                  const next = [...catalog.services];
                  const current = next[index];
                  if (!current) return;
                  next[index] = {
                    ...current,
                    monthlyPriceCents: Math.max(0, Math.round(Number(event.target.value) * 100)),
                  };
                  setCatalog({ ...catalog, services: next });
                }}
                step="1"
                type="number"
                value={service.monthlyPriceCents / 100}
              />
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function SessionCard({
  session,
  refresh,
}: {
  session: SessionSummary;
  refresh: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [qr, setQr] = useState("");
  const [editingCatalog, setEditingCatalog] = useState<SessionPublic | null>(null);
  const participantUrl = `${location.origin}/s/${session.slug}`;

  useEffect(() => {
    void QRCode.toDataURL(participantUrl, {
      margin: 1,
      width: 320,
      color: { dark: "#000000", light: "#ffffff" },
      errorCorrectionLevel: "M",
    }).then(setQr);
  }, [participantUrl]);

  const action = async (operation: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await operation();
      await refresh();
    } catch (operationError) {
      const code = operationError instanceof Error ? operationError.message : "ACTION_FAILED";
      setActionError(
        code === "ACTIVE_SESSION_EXISTS"
          ? "Ya hay una sesión activa. Ciérrala antes de abrir otra."
          : "No pudimos completar la acción. Intenta de nuevo.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className={`admin-session-card status-${session.status}`}>
      <div className="session-card-main">
        <div className="session-card-copy">
          <div className="session-title-row">
            <StatusBadge tone={session.status === "open" ? "green" : session.mode === "demo" ? "yellow" : "purple"}>
              {session.mode === "demo" ? "Demo" : statusLabel(session.status)}
            </StatusBadge>
            <code>{session.slug}</code>
          </div>
          <h2>{session.title}</h2>
          <div className="session-mini-stats">
            <span>
              <strong>{session.responseCount}</strong> respuestas
            </span>
            <span>
              Creada {new Date(session.createdAt).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
            </span>
          </div>
          <div className="session-links">
            <a href={`/s/${session.slug}`} target="_blank" rel="noreferrer">
              Participante ↗
            </a>
            <a href={`/live/${session.slug}`} target="_blank" rel="noreferrer">
              Proyección ↗
            </a>
            <a href={`/api/admin/sessions/${session.slug}/export?format=csv`}>
              Exportar CSV ↓
            </a>
          </div>
          <div className="session-actions">
            {session.status === "draft" && (
              <>
                <button
                  className="hw-btn hw-btn--primary"
                  disabled={busy}
                  onClick={() => void action(() => clientApi.openSession(session.slug))}
                  type="button"
                >
                  Abrir sesión
                </button>
                <button
                  className="hw-btn hw-btn--secondary"
                  disabled={busy}
                  onClick={() =>
                    void clientApi.getSession(session.slug).then(setEditingCatalog)
                  }
                  type="button"
                >
                  Editar precios
                </button>
              </>
            )}
            {session.status === "open" && (
              <button
                className="hw-btn hw-btn--danger"
                disabled={busy}
                onClick={() => {
                  if (confirm("¿Cerrar la sesión y congelar los resultados?")) {
                    void action(() => clientApi.closeSession(session.slug));
                  }
                }}
                type="button"
              >
                Cerrar y congelar
              </button>
            )}
            {session.status !== "open" && (
              <button
                className="text-button danger-text"
                disabled={busy}
                onClick={() => {
                  if (confirm("¿Eliminar esta sesión y sus respuestas? Esta acción no se puede deshacer.")) {
                    void action(() => clientApi.deleteSession(session.slug));
                  }
                }}
                type="button"
              >
                Eliminar
              </button>
            )}
          </div>
          {actionError && <p className="session-action-error">{actionError}</p>}
        </div>
        {qr && (
          <div className="qr-card">
            <img alt={`QR para entrar a ${session.title}`} src={qr} />
            <small>{participantUrl.replace(/^https?:\/\//, "")}</small>
          </div>
        )}
      </div>
      {editingCatalog && (
        <CatalogEditor
          onSaved={() => {
            setEditingCatalog(null);
            void refresh();
          }}
          session={editingCatalog}
        />
      )}
    </article>
  );
}

export function AdminPage() {
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);
  const [authFailure, setAuthFailure] = useState<"required" | "not-configured" | null>(null);
  const [title, setTitle] = useState("Semana Propedéutica · Mi vida digital");
  const [mode, setMode] = useState<"live" | "demo">("live");
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      setSessions(await clientApi.listSessions());
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "ADMIN_AUTH_REQUIRED");
    }
  };

  useEffect(() => {
    void clientApi
      .whoAmI()
      .then(({ email }) => {
        setAdminEmail(email);
        setAuthFailure(null);
        return load();
      })
      .catch((authError: unknown) => {
        setAdminEmail(null);
        setAuthFailure(
          authError instanceof Error && authError.message === "ADMIN_AUTH_NOT_CONFIGURED"
            ? "not-configured"
            : "required",
        );
      });
  }, []);

  useEffect(() => {
    if (!adminEmail) return;
    const requestedPath = new URLSearchParams(location.search).get("next");
    if (!requestedPath) return;

    const destination = new URL(requestedPath, location.origin);
    if (
      destination.origin === location.origin &&
      /^\/live\/[a-z0-9-]+$/i.test(destination.pathname)
    ) {
      location.replace(`${destination.pathname}${destination.search}${destination.hash}`);
    }
  }, [adminEmail]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreating(true);
    try {
      await clientApi.createSession(title, mode);
      await load();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "CREATE_FAILED");
    } finally {
      setCreating(false);
    }
  };

  if (authFailure) {
    return <AdminAccessFallback setupRequired={authFailure === "not-configured"} />;
  }
  if (!adminEmail || (!sessions && !error)) return <Loading label="Validando acceso…" />;

  return (
    <main className="admin-page" id="main">
      <header className="admin-header">
        <Brand />
        <div className="admin-identity">
          <span className="eyebrow">PANEL PRIVADO</span>
          <strong>{adminEmail}</strong>
          <a href="/cdn-cgi/access/logout">Cerrar sesión</a>
        </div>
      </header>
      <section className="admin-hero">
        <div>
          <span className="eyebrow">HELLO WORLD · EN VIVO</span>
          <h1>Sesiones</h1>
          <p>Crea la sala, verifica precios, proyecta el QR y abre cuando estés listo.</p>
        </div>
        <a className="hw-btn hw-btn--secondary" href="/demo" target="_blank" rel="noreferrer">
          Abrir plan B offline ↗
        </a>
      </section>
      {error && (
        <ErrorCard
          message={
            error === "ADMIN_AUTH_REQUIRED"
              ? "La administración requiere iniciar sesión mediante Cloudflare Access."
              : error
          }
          onRetry={() => void load()}
        />
      )}
      <section className="create-session section-card">
        <div className="admin-section-heading">
          <div>
            <span className="eyebrow">NUEVA SALA</span>
            <h2>Preparar una sesión</h2>
          </div>
          <span className="icon-box">＋</span>
        </div>
        <form onSubmit={create}>
          <label>
            Título
            <input
              maxLength={100}
              onChange={(event) => setTitle(event.target.value)}
              required
              value={title}
            />
          </label>
          <label>
            Datos
            <select onChange={(event) => setMode(event.target.value as "live" | "demo")} value={mode}>
              <option value="live">Reales · inicia vacía</option>
              <option value="demo">Demostración · 34 respuestas ficticias</option>
            </select>
          </label>
          <button className="hw-btn hw-btn--primary" disabled={creating} type="submit">
            {creating ? "Creando…" : "Crear sesión"}
          </button>
        </form>
      </section>
      <section className="sessions-list">
        <div className="list-heading">
          <h2>Sesiones recientes</h2>
          <span>{sessions?.length ?? 0} en total</span>
        </div>
        {sessions?.length ? (
          sessions.map((session) => <SessionCard key={session.id} refresh={load} session={session} />)
        ) : (
          <div className="empty-admin">
            <span>☁</span>
            <h3>Todavía no hay sesiones</h3>
            <p>Crea una arriba para generar el QR y el dashboard.</p>
          </div>
        )}
      </section>
      <footer className="admin-footer">
        <span>Los datos se eliminan 24 horas después de cerrar.</span>
        <span>Ejemplo del servidor: {formatMxn(7_000)} / mes.</span>
      </footer>
    </main>
  );
}
